import React, { useState } from 'react';
import { 
  ChevronDown, 
  ChevronUp, 
  Filter, 
  X, 
  SortAsc, 
  SortDesc,
  Check
} from 'lucide-react';
import { Button } from './ui/button';

// Sort options
const SORT_OPTIONS = [
  { value: 'name_asc', label: 'Alphabetical (A → Z)' },
  { value: 'name_desc', label: 'Alphabetical (Z → A)' },
  { value: 'newest', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'price_asc', label: 'Price: Low → High' },
  { value: 'price_desc', label: 'Price: High → Low' },
];

const ProductFilters = ({
  filterOptions,
  selectedCategories = [],
  selectedSort = 'name_asc',
  minPrice,
  maxPrice,
  selectedStatus,
  onCategoryChange,
  onSortChange,
  onPriceChange,
  onStatusChange,
  onClearFilters,
  loading = false,
  hideCategoryFilter = false,
}) => {
  const [showFilters, setShowFilters] = useState(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  
  // Use prop values directly for controlled inputs
  const localMinPrice = minPrice ?? '';
  const localMaxPrice = maxPrice ?? '';
  const [pendingMinPrice, setPendingMinPrice] = useState(localMinPrice);
  const [pendingMaxPrice, setPendingMaxPrice] = useState(localMaxPrice);

  // Count active filters (exclude categories if hidden)
  const activeFilterCount = 
    (hideCategoryFilter ? 0 : (selectedCategories?.length || 0)) + 
    (minPrice !== undefined && minPrice !== null ? 1 : 0) + 
    (maxPrice !== undefined && maxPrice !== null ? 1 : 0) + 
    (selectedStatus ? 1 : 0);

  const handlePriceApply = () => {
    onPriceChange(
      pendingMinPrice ? parseFloat(pendingMinPrice) : null,
      pendingMaxPrice ? parseFloat(pendingMaxPrice) : null
    );
  };

  const handleCategoryToggle = (categoryId) => {
    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(c => c !== categoryId)
      : [...selectedCategories, categoryId];
    onCategoryChange(newCategories);
  };

  const currentSortLabel = SORT_OPTIONS.find(o => o.value === selectedSort)?.label || 'Sort';

  return (
    <div className="bg-ivory-100 rounded-xl border border-ivory-300/50 shadow-luxury mb-6">
      {/* Mobile Filter Toggle */}
      <div className="lg:hidden p-4 border-b border-ivory-200">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="w-full flex items-center justify-between text-ivory-800 font-body"
        >
          <span className="flex items-center gap-2">
            <Filter className="w-5 h-5" />
            Filters & Sort
            {activeFilterCount > 0 && (
              <span className="px-2 py-0.5 text-xs bg-gold-500 text-white rounded-full">
                {activeFilterCount}
              </span>
            )}
          </span>
          {showFilters ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Filters Content */}
      <div className={`${showFilters ? 'block' : 'hidden'} lg:block p-4`}>
        <div className="flex flex-col lg:flex-row lg:items-center gap-4">
          {/* Sort Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowSortDropdown(!showSortDropdown)}
              className="flex items-center gap-2 px-4 py-2 bg-ivory-50 border border-ivory-300 rounded-lg hover:border-gold-500 transition-colors font-body text-sm w-full lg:w-auto"
              data-testid="sort-dropdown"
            >
              <SortAsc className="w-4 h-4 text-ivory-500" />
              <span className="text-ivory-700">{currentSortLabel}</span>
              <ChevronDown className="w-4 h-4 text-ivory-400 ml-auto lg:ml-2" />
            </button>
            
            {showSortDropdown && (
              <div className="absolute top-full left-0 mt-1 w-full lg:w-56 bg-ivory-50 border border-ivory-300 rounded-lg shadow-luxury z-20">
                {SORT_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      onSortChange(option.value);
                      setShowSortDropdown(false);
                    }}
                    className={`w-full px-4 py-2 text-left text-sm font-body hover:bg-gold-500/10 flex items-center justify-between ${
                      selectedSort === option.value ? 'text-gold-600 bg-gold-500/5' : 'text-ivory-700'
                    }`}
                    data-testid={`sort-option-${option.value}`}
                  >
                    {option.label}
                    {selectedSort === option.value && <Check className="w-4 h-4" />}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Category Filter */}
          {!hideCategoryFilter && filterOptions?.categories?.length > 0 && (
            <div className="relative">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="flex items-center gap-2 px-4 py-2 bg-ivory-50 border border-ivory-300 rounded-lg hover:border-gold-500 transition-colors font-body text-sm w-full lg:w-auto"
                data-testid="category-filter"
              >
                <Filter className="w-4 h-4 text-ivory-500" />
                <span className="text-ivory-700">
                  Category
                  {selectedCategories.length > 0 && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-gold-500 text-white rounded">
                      {selectedCategories.length}
                    </span>
                  )}
                </span>
                <ChevronDown className="w-4 h-4 text-ivory-400 ml-auto lg:ml-2" />
              </button>
              
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 mt-1 w-full lg:w-64 bg-ivory-50 border border-ivory-300 rounded-lg shadow-luxury z-20 max-h-64 overflow-y-auto">
                  {filterOptions.categories.map((cat) => (
                    <label
                      key={cat.id}
                      className="flex items-center gap-3 px-4 py-2 hover:bg-gold-500/10 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={selectedCategories.includes(cat.id)}
                        onChange={() => handleCategoryToggle(cat.id)}
                        className="w-4 h-4 rounded border-ivory-300 text-gold-500 focus:ring-gold-500"
                      />
                      <span className="text-sm text-ivory-700 font-body flex-1">{cat.name}</span>
                      <span className="text-xs text-ivory-400">{cat.count}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Price Range Filter */}
          {filterOptions?.price_range?.min !== null && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-ivory-500 font-body">Price:</span>
              <input
                type="number"
                placeholder="Min"
                value={pendingMinPrice}
                onChange={(e) => setPendingMinPrice(e.target.value)}
                className="w-20 px-2 py-1.5 text-sm border border-ivory-300 rounded-lg bg-ivory-50 font-body"
                data-testid="price-min"
              />
              <span className="text-ivory-400">-</span>
              <input
                type="number"
                placeholder="Max"
                value={pendingMaxPrice}
                onChange={(e) => setPendingMaxPrice(e.target.value)}
                className="w-20 px-2 py-1.5 text-sm border border-ivory-300 rounded-lg bg-ivory-50 font-body"
                data-testid="price-max"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={handlePriceApply}
                className="text-xs"
                data-testid="price-apply"
              >
                Apply
              </Button>
            </div>
          )}

          {/* Status Filter */}
          {filterOptions?.statuses?.length > 1 && (
            <select
              value={selectedStatus || ''}
              onChange={(e) => onStatusChange(e.target.value || null)}
              className="px-3 py-2 text-sm border border-ivory-300 rounded-lg bg-ivory-50 font-body"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              {filterOptions.statuses.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() + status.slice(1)}
                </option>
              ))}
            </select>
          )}

          {/* Clear Filters */}
          {activeFilterCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onClearFilters}
              className="text-ivory-500 hover:text-red-500"
              data-testid="clear-filters"
            >
              <X className="w-4 h-4 mr-1" />
              Clear All
            </Button>
          )}

          {/* Loading indicator */}
          {loading && (
            <div className="ml-auto">
              <div className="w-5 h-5 border-2 border-gold-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
        </div>

        {/* Selected Categories Tags */}
        {!hideCategoryFilter && selectedCategories.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-ivory-200">
            <span className="text-sm text-ivory-500 font-body">Filtering by:</span>
            {selectedCategories.map((catId) => {
              const cat = filterOptions?.categories?.find(c => c.id === catId);
              return (
                <span
                  key={catId}
                  className="inline-flex items-center gap-1 px-2 py-1 bg-gold-500/10 text-gold-700 rounded-md text-xs font-body"
                >
                  {cat?.name || catId}
                  <button
                    onClick={() => handleCategoryToggle(catId)}
                    className="hover:text-red-500"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              );
            })}
          </div>
        )}
      </div>

      {/* Click outside handler */}
      {(showSortDropdown || showCategoryDropdown) && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => {
            setShowSortDropdown(false);
            setShowCategoryDropdown(false);
          }}
        />
      )}
    </div>
  );
};

export default ProductFilters;
